// ToolBar.tsx
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus, faTrash, faFilterCircleXmark } from '@fortawesome/free-solid-svg-icons';
import WarningBox from './WarningBox'
import Dropdown from './dropdown/dropdown'
import '../css_files/ToolBar.css';
import { useApiData } from '../../contexts/apiService';
import { useUserAccess } from '../../contexts/userAccess';

interface Column {
	header: string;
	accessor: string;
	type?: 'text' | 'number' | 'dropdown' | 'date';
    options?: Array<{ value: string; label: string }>;
    required?: boolean;
    getDynamicOptions?: (apiData: any, inputs: Record<string, string>) => Array<{ value: string; label: string }>;
}

interface ToolBarProps {
	columns: Column[];
    onAdd: (newItem: Record<string, string>) => void;
	handleDeleteRow: () => void;
	selectedTicketsCount: number;
	activeTable: string;
    isCarrierTable?: boolean;
}

const VALID_MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June', 
    'July', 'August', 'September', 'October', 'November', 'December'
];
const CURRENT_YEAR = new Date().getFullYear() + 1;
const MIN_YEAR = 2019;

const ToolBar: React.FC<ToolBarProps> = ({ columns, onAdd, handleDeleteRow, selectedTicketsCount, activeTable, isCarrierTable = false}) => {
	const { apiData } = useApiData();
    const { hasAdminAccess } = useUserAccess();
    const [inputs, setInputs] = useState<Record<string, string>>({});
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showAdminDeleteWarning, setShowAdminDeleteWarning] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const requiresAllFields = ['producer_pricing', 'market_discounts', 'nominations'].includes(activeTable);

    const optionalFields = activeTable === 'market_discounts' ? ['offload_lane'] : [];

    // Get month options for dropdowns
    const monthOptions = VALID_MONTHS.map(month => ({
        value: month,
        label: month
    }));

    // Get year options from 2020 to current year
    const yearOptions = Array.from(
        { length: CURRENT_YEAR - MIN_YEAR + 1 }, 
        (_, i) => ({ 
            value: String(MIN_YEAR + i), 
            label: String(MIN_YEAR + i) 
        })
    ).reverse();

    const handleDeleteClick = () => {
		if (isCarrierTable) {
			if (!hasAdminAccess && selectedTicketsCount > 0) {
				// Show warning if not admin
				setShowAdminDeleteWarning(true);
			} else if (hasAdminAccess && selectedTicketsCount > 0) {
				setShowDeleteConfirm(true);
			}
		} else if (!isCarrierTable && selectedTicketsCount > 0) {
			setShowDeleteConfirm(true);
		}
	};

    const handleConfirmDelete = () => {
        handleDeleteRow();
        setShowDeleteConfirm(false);
        setShowAdminDeleteWarning(false);
    };

	// Reset inputs when activeTable changes
    useEffect(() => {
        setInputs({});
        setErrors({});
        setShowAdminDeleteWarning(false);
    }, [activeTable]);

	const handleInputChange = (accessor: string, value: string) => {
        // Special validation for month and year fields
        if (accessor === 'month' && value && !VALID_MONTHS.includes(value)) {
            setErrors(prev => ({
                ...prev,
                month: 'Invalid month name. Please select from dropdown.'
            }));
            return;
        }

        if (accessor === 'year') {
            const yearNum = parseInt(value, 10);
            if (value && (isNaN(yearNum) || yearNum < MIN_YEAR || yearNum > CURRENT_YEAR)) {
                setErrors(prev => ({
                    ...prev,
                    year: `Year must be between ${MIN_YEAR} and ${CURRENT_YEAR}`
                }));
                return;
            }
        }

        setInputs(prev => {
            const updatedInputs = { ...prev, [accessor]: value };
            // If station_owner changes, reset station only for market_discounts table
            if (accessor === 'station_owner' && (activeTable === 'market_discounts')) {
                updatedInputs['station'] = '';
            }
    
            return updatedInputs;
        });

        // Clear error when user starts typing
        if (errors[accessor]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[accessor];
                return newErrors;
            });
        }
    };

    const validateInputs = () => {
        const newErrors: Record<string, string> = {};
        
        columns.forEach(column => {
            // Check if this field is in the optional fields list
            const isOptionalField = optionalFields.includes(column.accessor);
            
            // For carrier tables, all fields are required + tables on requiresAllFields (except optional fields)
            const isFieldRequired = (isCarrierTable || requiresAllFields || column.required) && !isOptionalField;
            const value = inputs[column.accessor] || '';

            if (isFieldRequired && !value.trim()) {
                newErrors[column.accessor] = `${column.header} is required`;
            }
            
            // Special validation for month and year
            if (value.trim()) {
                if (column.accessor === 'month' && !VALID_MONTHS.includes(value)) {
                    newErrors[column.accessor] = 'Invalid month name';
                }
                
                if (column.accessor === 'year') {
                    const yearNum = parseInt(value, 10);
                    if (isNaN(yearNum) || yearNum < MIN_YEAR || yearNum > CURRENT_YEAR) {
                        newErrors[column.accessor] = `Year must be between ${MIN_YEAR} and ${CURRENT_YEAR}`;
                    }
                }
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

	const handleAdd = () => {
        if (validateInputs()) {
            onAdd(inputs);
            setInputs({});
            setErrors({});
        }
    };

	const hasAnyInput = Object.values(inputs).some(value => value.trim() !== '');

    const isAddDisabled = () => {
        if (!hasAnyInput) return true;
        
        if (isCarrierTable || requiresAllFields) {
            return !columns.every(col => {
                const isOptionalField = optionalFields.includes(col.accessor);
                return isOptionalField || inputs[col.accessor]?.trim();
            });
        }
        
        return false;
    };

	const renderInput = (column: Column) => {

        const isOptionalField = optionalFields.includes(column.accessor);
        const isRequired = (isCarrierTable || requiresAllFields || column.required) && !isOptionalField;

        // Modify options for month and year fields
        let options = column.options || [];
        if (column.accessor === 'month') {
            options = monthOptions;
        } else if (column.accessor === 'year') {
            options = yearOptions;

        } else if (column.type === 'dropdown') {
            // Apply filtering ONLY when the active table is 'market_discounts'
            if (column.accessor === 'station' && (activeTable === 'market_discounts')) {
                options = options.filter(option => {
                    const selectedOwner = inputs['station_owner'];
                    return !selectedOwner || apiData?.market_coordinates?.some(
                        item => item.market === selectedOwner && item.station === option.value
                    );
                });
            }
            
            if (column.getDynamicOptions && apiData) {
                options = column.getDynamicOptions(apiData, inputs);
            }
        }

        // Force dropdown for month and year fields
        const fieldType = column.accessor === 'month' || column.accessor === 'year' 
            ? 'dropdown' 
            : column.type;

        if (fieldType === 'dropdown') {
            return (
                <div className="input-wrapper">
                    <Dropdown
                        options={options}
                        value={inputs[column.accessor] || ''}
                        onChange={(value) => handleInputChange(column.accessor, value)}
                        placeholder={`${column.header}${isRequired ? ' *' : ''}`}
                        isDisabled={false}
                    />
                    {errors[column.accessor] && (
                        <div className="error-message">{errors[column.accessor]}</div>
                    )}
                </div>
            );
        } else if (column.type === 'date') {
            return (
                <div className="input-wrapper">
                    <input
                        type="date"
                        placeholder={`${column.header}${isCarrierTable || column.required ? ' *' : ''}`}
                        value={inputs[column.accessor] || ''}
                        onChange={(e) => handleInputChange(column.accessor, e.target.value)}
                        className={`tb-input ${errors[column.accessor] ? 'error' : ''}`}
                    />
                    {errors[column.accessor] && (
                        <div className="error-message">{errors[column.accessor]}</div>
                    )}
                </div>
            );
        } else {
            return (
                <div className="input-wrapper">
                    <input
                        type={column.type === 'number' ? 'number' : 'text'}
                        placeholder={`${column.header}${isCarrierTable || column.required ? ' *' : ''}`}
                        value={inputs[column.accessor] || ''}
                        onChange={(e) => handleInputChange(column.accessor, e.target.value)}
                        className={`tb-input ${errors[column.accessor] ? 'error' : ''}`}
                    />
                    {errors[column.accessor] && (
                        <div className="error-message">{errors[column.accessor]}</div>
                    )}
                </div>
            );
        }
    };     

	return (
        <div className="tb-main">
            <button onClick={handleAdd} disabled={isAddDisabled()}>
                <span>
                    <FontAwesomeIcon 
                        icon={faCirclePlus} 
                        style={{ 
                            color: isAddDisabled() ? 'gray' : 'green', 
                            height: 25 
                        }}
                    />
                </span>
            </button>
            
            {columns.map(column => {
                if (activeTable === 'name_corrections' &&
                    column.accessor === 'key2' &&
                    inputs['type'] !== 'lease'
                ) {
                    return null;
                }
                return (
                    <div key={column.header} className="tb-input-container">
                        {renderInput(column)}
                    </div>
                );
            })}

            <button onClick={handleDeleteClick} disabled={selectedTicketsCount === 0}>
                <span>
                    <FontAwesomeIcon 
                        icon={faTrash} 
                        style={{ 
                            color: selectedTicketsCount > 0 ? 'red' : 'gray', 
                            height: 25 
                        }}
                    />
                </span>
            </button>
            
            {/* Admin Delete Warning for Carrier Tables */}
			{isCarrierTable && !hasAdminAccess && selectedTicketsCount > 0 && showAdminDeleteWarning && (
				<WarningBox
                    isOpen={showAdminDeleteWarning}
                    onClose={() => setShowAdminDeleteWarning(false)}
                    title="Deletion Denied"
                    message={`You need to be an admin to delete a carrier ticket.`}
                    showbutton2={false}                    
                />
			)}
            
            <WarningBox
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={handleConfirmDelete}
                title="Confirm Deletion"
                message={`Are you sure you want to delete ${selectedTicketsCount} selected ${
                    selectedTicketsCount === 1 ? 'row' : 'rows'
                }? This action cannot be undone.`}
                button1='Cancel'
                button2='Delete'
            />
        </div>
    );
};

export default ToolBar;